import { Button, useMediaQuery } from "@mui/material";
import { useCallback, useEffect, useRef, useState } from "react";
import Webcam from "react-webcam";
import GradientSuccessMarkIcon from "../../../assets/svgs/GradientSuccessMarkIcon";
import LoadingApp from "../../../components/LoadingApp";
import { BiometriaStatusEnum } from "../../../interfaces/enum/BiometriaStatusEnum";
import { CameraAcessProps } from "../../../interfaces/props/SaudeOnline/CameraAcessProps";
import { cadastrarBiometriaFacial, compararBiometriaFacial } from "../../../services/BiometriaFacial/BiometriaFacialService";
import ErrorIcon from "./ErrorIcon";
import { videoConstraints } from "./defaultStates";
import './styles.css';
import { sxButton, sxTryAgainButton } from "./sxProps";
import { useAccessibility } from "../../../Store/contexts/AccessibilityContext";

function CameraAcess(props: CameraAcessProps) {
    const [loading, setLoading] = useState<boolean>(false);
    const [biometriaStatus, setBiometriaStatus] = useState<BiometriaStatusEnum>(BiometriaStatusEnum.Capturando);
    const [message, setMessage] = useState<string>('');
    const [back, setBack] = useState<boolean>(false);
    const [count, setCount] = useState<number>(5);
    const [countErrors, setCountErros] = useState<number>(0);
    const [startCounting, setStartCounting] = useState<boolean>(false)
    const webcamRef = useRef<Webcam>(null);
    const landscapeMobile = useMediaQuery('(max-width: 1130px) and (max-height: 600px)');

    const { fontSizeStyleAccessibility } = useAccessibility();

    const [devices, setDevices] = useState<CameraDeviceProps[]>([]);
    
    const handleDevices = useCallback(
      (mediaDevices: MediaDeviceInfo[]) =>
        setDevices(mediaDevices.filter(({ kind }) => kind === "videoinput")),
      []
    );
    
    useEffect(() => {
      navigator.mediaDevices.enumerateDevices().then(handleDevices);
    }, [handleDevices]);
    
    useEffect(() => {
        if (startCounting) {
            setTimeout(() => {
                if (count > 0) {
                    setCount(count - 1);
                }
            }, 1000);
        }
        if (count === 0) {
            props.handleValidation();
        }
    }, [count, startCounting]);

    const capture = useCallback(() => {

        var width = window.innerWidth;
        var height = window.innerHeight;
        var imageSrc:any;

        if (height > width) {
            imageSrc= webcamRef?.current?.getScreenshot({width: 354, height: 472});
        } else {
            imageSrc= webcamRef?.current?.getScreenshot();
        }     

        if (imageSrc) {
            setLoading(true);

            cadastrarBiometriaFacial(imageSrc)?.then(response => {

                if (response?.result) {
                    const data = { ...response, result: JSON.parse(response?.result) };

                    if (data.result.error) {
                        setBiometriaStatus(BiometriaStatusEnum.Erro);
                        if (data.result.error === 'Identificador informado já possui cadastro na plataforma.') {
                            setBack(true)
                        }
                        if (countErrors < 2) {
                            setMessage(data.result.error)
                        }
                        else {
                            setMessage('Houve uma falha com o sistema de autenticação de biometria facial, você poderá cadastrar sua biometria mais tarde.')
                            setBack(true);
                        }

                        setLoading(false);
                        return;
                    }
                    compararBiometriaFacial(imageSrc)?.then(response => {
                        if (response?.result === 'Beneficiário não reconhecido.') {
                            setBiometriaStatus(BiometriaStatusEnum.Erro);
                            setMessage(data.result.error)
                            setLoading(false);
                        }
                        else {
                            setStartCounting(true)
                            setBiometriaStatus(BiometriaStatusEnum.Sucesso);
                            setLoading(false);
                        }
                    })
                }
            })
        }
    }, [webcamRef, countErrors]);

    const tryAgain = () => {
        if (countErrors < 2) {
            setCountErros(countErrors + 1)
        }
        else {
            setCountErros(0);
        }
        setBiometriaStatus(BiometriaStatusEnum.Capturando);
        setStartCounting(false);
        setCount(5);
    }

    const CaptureMessage = () => (
        <p className="camera-instructions" style={fontSizeStyleAccessibility} tabIndex={0}>
            Posicione seu rosto dentro da linha pontilhada, olhe para a câmera e clique no botão abaixo.
        </p>
    )

    const restartRegistartion = () => {
        setCountErros(0)
        setBiometriaStatus(BiometriaStatusEnum.Capturando);
        setStartCounting(false);
        setBack(false)
        setCount(5);
    }

    return (

        <div className='camera-holder'>
            {loading ? <LoadingApp text={'Analisando imagem, aguarde...'} showLoading={loading} /> :
                <>
                    <div className="camera-capture">
                        {biometriaStatus === BiometriaStatusEnum.Erro && !loading &&
                            <div className="camera-result-holder">
                                <div className="icon-holder">
                                    <ErrorIcon />
                                </div>
                                <p className="camera-instructions">
                                    Falha de comunicação
                                </p>
                                <p className="countdown">
                                    {message}
                                </p>
                            </div>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Sucesso &&
                            <div className="camera-result-holder">
                                <div className="icon-holder">
                                    <GradientSuccessMarkIcon />
                                </div>
                                <p className="camera-instructions">
                                    Biometria cadastrada com sucesso!
                                </p>
                                <p className="countdown">
                                    (Essa tela será fechada em {count}s)
                                </p>
                            </div>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Capturando && <div className="face-orientation" />}
                        {devices.length > 0 && 
                        <Webcam
                            ref={webcamRef}
                            mirrored={true}
                            audio={false}
                            height={'100%'}
                            width={'845px'}
                            className="camera"
                            screenshotFormat="image/jpeg"
                            videoConstraints={{
                                width: 1280,
                                height: 720,
                                facingMode: "user",
                                deviceId: devices[0].deviceId
                            }}
                        />}
                        {biometriaStatus === BiometriaStatusEnum.Capturando && !landscapeMobile &&
                            <CaptureMessage />
                        }
                    </div>
                    <div className="camera-button">
                        {landscapeMobile && <CaptureMessage />}
                        {biometriaStatus === BiometriaStatusEnum.Capturando &&
                            <Button
                                variant="contained"
                                size="large"
                                onClick={capture}
                                sx={sxButton}
                                fullWidth>
                                CAPTURAR FOTO DE SEGURANÇA
                            </Button>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && !back &&
                            <Button
                                variant="contained"
                                size="large"
                                onClick={tryAgain}
                                sx={sxButton}
                                fullWidth>
                                Tentar novamente
                            </Button>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && back &&
                            <Button
                                variant="contained"
                                size="large"
                                onClick={props?.handleContinue}
                                sx={sxButton}
                                fullWidth>
                                Continuar
                            </Button>
                        }
                        {biometriaStatus === BiometriaStatusEnum.Erro && back &&
                            <div className="try-again-button">
                                <Button
                                    variant="outlined"
                                    size="large"
                                    onClick={restartRegistartion}
                                    sx={sxTryAgainButton}
                                    fullWidth>
                                    Tentar novamente
                                </Button>
                            </div>
                        }
                    </div>
                </>
            }

        </div>
    );
}

export default CameraAcess;